import styled from "styled-components"
import { ButtonPrimary, ButtonSecondarySoftLarge } from "~styled/button"
import colors from "~constants/colors"

interface ValidationActionsProps {
  isSaving: boolean
  onDiscard: () => void
  onInvalidate: () => void
  onValidate: () => void
  // Release ("Soltar") hands the claim back: VALIDATING -> CREATED. Only shown
  // when the current operator owns the claim (or is an admin).
  onRelease?: () => void
  canRelease?: boolean
}

const ValidationActions = ({
  isSaving,
  onDiscard,
  onInvalidate,
  onValidate,
  onRelease,
  canRelease,
}: ValidationActionsProps) => (
  <ActionBar>
    <Hints>
      <Hint>
        <Kbd>J</Kbd>
        <Kbd>K</Kbd> navigate
      </Hint>
      <Hint>
        <Kbd>A</Kbd> accept AI
      </Hint>
      {canRelease && (
        <Hint>
          <Kbd>R</Kbd> release
        </Hint>
      )}
    </Hints>
    <ActionsGroup>
      {canRelease && onRelease && (
        <ButtonSecondarySoftLarge disabled={isSaving} onClick={onRelease}>
          Release
          <BtnKbd>R</BtnKbd>
        </ButtonSecondarySoftLarge>
      )}
      <ButtonSecondarySoftLarge disabled={isSaving} onClick={onDiscard}>
        Discard (no email)
        <BtnKbd>D</BtnKbd>
      </ButtonSecondarySoftLarge>
      <ButtonPrimary disabled={isSaving} onClick={onInvalidate}>
        Invalidate
        <BtnKbd>I</BtnKbd>
      </ButtonPrimary>
      <ButtonPrimary
        disabled={isSaving}
        style={{ backgroundColor: colors.green }}
        onClick={onValidate}
      >
        Validate
        <BtnKbd>⏎</BtnKbd>
      </ButtonPrimary>
    </ActionsGroup>
  </ActionBar>
)

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid ${colors.gray2};
  background: ${colors.white};
  flex-shrink: 0;
`

const ActionsGroup = styled.div`
  display: flex;
  gap: 10px;
`

const Hints = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12px;
  color: ${colors.gray6};
`

const Hint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const Kbd = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: ${colors.gray6};
  background: ${colors.gray1};
  border: 1px solid ${colors.gray2};
  border-radius: 3px;
`

// Keycap shown inside an action button. Inherits the button's text colour via
// currentColor so it reads correctly on both the solid (Validate/Invalidate)
// and the soft (Discard) buttons, with a little breathing room from the label.
const BtnKbd = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 10px;
  padding: 0 5px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: currentColor;
  border: 1px solid currentColor;
  border-radius: 4px;
  opacity: 0.65;
`

export default ValidationActions
